'use client';
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useSyncExternalStore
} from 'react';
import { useRouter } from 'next/navigation';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import WindowSize from '@/utils/windowSize';
import {
  useProduct,
  useResolvedSearchParams,
  useSelectedVariant
} from '@/components/product/product-context';
import ProductCard, { CARD_INDEX } from './ProductCard';
import getPriceDifference from './get-price-difference';
import offsetBlockClass from '@/utils/offsetBlockClass';
import {
  getUpsellToggleSourceProduct,
  getUpsellToggleSourceProductSnapshot,
  storeUpsellToggleSourceProduct,
  subscribeToUpsellToggleSourceProduct
} from './source-product-storage';

/**
 * A toggle of two product cards: the main product plus one sibling, either an
 * upsell or a downsell. Shows [main, upsell], [downsell, main], or nothing.
 *
 * Which sibling sits next to the main product, on which side, and whether the
 * toggle appears at all depends on the `upgrade` search param, so it needs a
 * Suspense boundary.
 */
export default function UpsellToggle({ block, position }) {
  const sectionRef = useRef(null);
  const { productData } = useProduct();
  const [selectedIndex, setSelectedIndex] = useState(CARD_INDEX.main);

  const {
    main_product,
    upsell_product,
    downsell_product,
    response_uid,
    label,
    show_offset_block_selector: showOffsetBlock
  } = block.upsell_toggle.reference[0];

  const products = {
    main: {
      ...main_product,
      shopifyProduct: [productData?.shopifyProduct],
      url: `/products/${productData?.shopifyProduct?.handle}`
    },
    upsell: toSiblingProduct(upsell_product),
    downsell: toSiblingProduct(downsell_product)
  };

  function handleClick(index) {
    setSelectedIndex(index);
  }

  // Clears the selection on unmount so a back/forward to this PDP starts on the
  // main card again.
  useLayoutEffect(() => {
    return () => {
      setSelectedIndex(CARD_INDEX.main);
    };
  }, []);

  const sectionProps = { response_uid, position, label, showOffsetBlock };
  const hasUpsell = !!products.upsell;

  // Nothing to toggle to, so there's no toggle
  if (!hasUpsell && !products.downsell) return null;

  return (
    <Suspense
      fallback={
        <ToggleFallback
          hasUpsell={hasUpsell}
          products={products}
          showOffsetBlock={showOffsetBlock}
          sectionProps={sectionProps}
          sectionRef={sectionRef}
        />
      }
    >
      <ResolvedToggle
        products={products}
        showOffsetBlock={showOffsetBlock}
        selectedIndex={selectedIndex}
        handleClick={handleClick}
        sectionProps={sectionProps}
        hasUpsell={hasUpsell}
        sectionRef={sectionRef}
      />
    </Suspense>
  );
}

/**
 * Shapes a referenced upsell/downsell entry into a card's product.
 */
function toSiblingProduct(reference) {
  const shopifyProduct = reference?.product;
  const url = reference?.url; // Safest for this to come from CS, not shopify

  if (!shopifyProduct?.title || !url) return null;

  return {
    ...reference,
    shopifyProduct: [shopifyProduct],
    url
  };
}

function SectionContent({
  response_uid,
  position,
  label,
  showOffsetBlock,
  sectionRef,
  children
}) {
  return (
    <section
      id={`${response_uid}-${position}`}
      data-name="upsell-toggle"
      ref={sectionRef}
      className={offsetBlockClass(
        showOffsetBlock,
        'scroll-mt-18.75 not-last:mb-8 sm:scroll-mt-16.25'
      )}
    >
      <div className="sh12 mb-2 text-white">{label}</div>
      <div className="grid grid-cols-2 gap-x-3">{children}</div>
    </section>
  );
}

function ToggleFallback({
  hasUpsell,
  products,
  showOffsetBlock,
  sectionProps,
  sectionRef
}) {
  if (!hasUpsell) return null;

  return (
    <SectionContent {...sectionProps} sectionRef={sectionRef}>
      <CardPairSkeleton products={products} showOffsetBlock={showOffsetBlock} />
    </SectionContent>
  );
}

function CardPairSkeleton({ products, showOffsetBlock }) {
  return (
    <>
      <ProductCardSkeleton
        productData={products.main}
        showOffsetBlock={showOffsetBlock}
      />
      <ProductCardSkeleton
        productData={products.upsell}
        showOffsetBlock={showOffsetBlock}
      />
    </>
  );
}

function ProductCardSkeleton({ productData, showOffsetBlock }) {
  const hasTag = !!productData?.tag_text;
  const borderClasses = showOffsetBlock
    ? 'border-parlor-green-80 bg-parlor-green-115'
    : 'border-parlor-green-80';

  return (
    <div className="group grid" {...SKELETON_PROPS}>
      {hasTag && (
        <div className="relative grid grid-cols-2 gap-x-1">
          <div className="bg-light-green h-5 w-24 rounded-sm" />
        </div>
      )}
      <div
        className={`border-2 ${borderClasses} flex flex-col justify-between`}
      >
        <div className="px-2 pt-2 pl-3">
          <div className="flex w-full justify-between gap-x-2">
            <div className="sh12 flex min-h-lh flex-1 items-center">
              <div className="bg-parlor-green-80 h-4 w-full max-w-24 rounded-sm" />
            </div>
            <div className="bg-parlor-green-80 z-20 h-4 w-4 rounded-full border border-white" />
          </div>
          <div className="min-h-6">
            <div className="price-r-label flex min-h-lh items-center">
              <div className="bg-parlor-green-80 h-4 w-20 rounded-sm" />
            </div>
          </div>
        </div>
        <div>
          <div className="min-h-15.75 w-full" />
          <div className="px-2 pb-2">
            <div className="body-s flex min-h-lh items-center first:mb-1">
              <div className="bg-parlor-green-80 h-4 w-full rounded-sm" />
            </div>
            <div className="body-s flex min-h-lh items-center">
              <div className="bg-parlor-green-80 h-4 w-11/12 rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Suspends on search params, then renders the toggle when it should exist.
 */
function ResolvedToggle({
  hasUpsell,
  sectionProps,
  sectionRef,
  products,
  showOffsetBlock,
  selectedIndex,
  handleClick
}) {
  const upgradeParam = useUpgradeParam();
  const sourceProductHandle = useSourceProductHandle();
  const { selectedOptions, variant } = useSelectedVariant();
  useScrollToUpsell(upgradeParam, sectionRef);

  const showDownsell = upgradeParam !== null && upgradeParam !== 'main';
  const destinationHandle = products.main?.shopifyProduct?.[0]?.handle;
  const shouldResolveStoredDownsellProduct = Boolean(
    showDownsell && sourceProductHandle && destinationHandle
  );

  const storedSourceProductSnapshot = useSyncExternalStore(
    (listener) =>
      subscribeToUpsellToggleSourceProduct(
        {
          destinationHandle,
          sourceHandle: sourceProductHandle
        },
        listener
      ),
    () =>
      getUpsellToggleSourceProductSnapshot({
        destinationHandle,
        sourceHandle: sourceProductHandle
      }),
    () => null
  );

  const storedDownsellProduct = shouldResolveStoredDownsellProduct
    ? getUpsellToggleSourceProduct({
        destinationHandle,
        sourceHandle: sourceProductHandle,
        serializedSourceProduct: storedSourceProductSnapshot
      })
    : null;

  if (!hasUpsell && upgradeParam === null) return null;

  const activeDownsellProduct = storedDownsellProduct || products.downsell;

  const selection = {
    size: selectedOptions?.size,
    cover: selectedOptions?.cover,
    variant
  };

  return (
    <SectionContent {...sectionProps} sectionRef={sectionRef}>
      <CardPair
        products={products}
        downsellProduct={activeDownsellProduct}
        showOffsetBlock={showOffsetBlock}
        selectedIndex={selectedIndex}
        handleClick={handleClick}
        showDownsell={showDownsell}
        selection={selection}
      />
    </SectionContent>
  );
}

function useUpgradeParam() {
  const searchParams = useResolvedSearchParams();
  const rawUpgrade = searchParams?.upgrade;

  return (Array.isArray(rawUpgrade) ? rawUpgrade[0] : rawUpgrade) ?? null;
}

function useSourceProductHandle() {
  const searchParams = useResolvedSearchParams();
  const rawSourceHandle = searchParams?.source;

  return (
    (Array.isArray(rawSourceHandle) ? rawSourceHandle[0] : rawSourceHandle) ??
    null
  );
}

/**
 * Presentational card pair.
 */
function CardPair({
  products,
  downsellProduct,
  showOffsetBlock,
  selectedIndex,
  handleClick,
  showDownsell,
  selection
}) {
  const router = useRouter();
  const {
    cover: selectedCover,
    size: variantSize,
    variant: selectedVariant
  } = selection;
  const siblingType = showDownsell ? 'downsell' : 'upsell';
  const siblingProduct = showDownsell ? downsellProduct : products.upsell;
  const sourceHandle = products.main?.shopifyProduct?.[0]?.handle;

  const mainCard = (
    <ProductCard
      showOffsetBlock={showOffsetBlock}
      productData={products.main}
      selectedIndex={selectedIndex}
      type="main"
      variantSize={variantSize}
      showDownsellCard={showDownsell}
      onCardClick={() => handleClick(CARD_INDEX.main)}
    />
  );

  const siblingCard = siblingProduct ? (
    <ProductCard
      showOffsetBlock={showOffsetBlock}
      productData={siblingProduct}
      selectedIndex={selectedIndex}
      type={siblingType}
      variantSize={variantSize}
      priceDifference={getPriceDifference({
        product: siblingProduct,
        selectedVariant,
        variantSize
      })}
      showDownsellCard={showDownsell}
      onCardClick={() => {
        handleClick(CARD_INDEX[siblingType]);
        const destinationHandle = siblingProduct?.shopifyProduct?.[0]?.handle;

        if (siblingType === 'upsell' && destinationHandle && sourceHandle) {
          storeUpsellToggleSourceProduct({
            destinationHandle,
            sourceProduct: products.main
          });
        }

        router.push(
          buildSiblingUrl({
            product: siblingProduct,
            type: siblingType,
            variantSize,
            selectedCover,
            sourceHandle
          })
        );
      }}
    />
  ) : null;

  const firstCard = showDownsell ? siblingCard : mainCard;
  const secondCard = showDownsell ? mainCard : siblingCard;

  return (
    <>
      {firstCard}
      {secondCard}
    </>
  );
}

/**
 * Carries the current size and cover over to the sibling product's PDP, plus
 * the `upgrade` flag that tells it which way the user travelled.
 */
function buildSiblingUrl({
  product,
  type,
  variantSize,
  selectedCover,
  sourceHandle
}) {
  const upgradeValue = type === 'upsell' ? 'true' : 'main';
  const cover = product?.attribute_override || selectedCover;
  const params = new URLSearchParams();

  params.set('upgrade', upgradeValue);
  if (variantSize) params.set('size', variantSize);
  if (cover) params.set('cover', cover);
  if (type === 'upsell' && sourceHandle) {
    params.set('source', sourceHandle);
  }

  return `${product?.url}?${params.toString()}`;
}

/**
 * Arriving from a sibling product on mobile drops the user further down the
 * page, so pull the toggle back into view.
 */
function useScrollToUpsell(upgrade, sectionRef) {
  const windowSize = WindowSize();
  const onMobile = windowSize.width <= 1024;

  useEffect(() => {
    if (!onMobile || upgrade === null) return;

    sectionRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [onMobile, upgrade, sectionRef]);
}
